import clsx from 'clsx';
import * as React from 'react';

import {
    BLOCK_COLUMN,
    BLOCK_DIFF,
    BLOCK_DIVIDER,
    BLOCK_EXPANDABLE,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS_ITEM,
    conflicts,
    FRAGMENT_EXPANDABLE_TITLE,
    LIST_BLOCKS,
    list,
} from '@gitbook/doc-core';
import type { PageLayoutOptionsWidth } from '@gitbook/hive-core';
import { type AncestorNode, Block, type Document, type Fragment } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';
import type { ButtonProps } from '@gitbook/spine-react';
import type { List } from '@gitbook/util-immutable';
import { assert } from '@gitbook/util-ts';

import type { LayoutRenderModes } from '../../BlockLayout';
import { getBlockLineHeight } from './heights';
import { getBlockSpacing } from './spacing';
import { FULL_WIDTH_BLOCK_TYPES } from './useBlockLayoutModeCommands';

export interface BlockLayout {
    /**
     * Spacing for the block itself.
     */
    blockStyle: { style?: React.CSSProperties; className: string };
    /**
     * Control buttons (options and insertion)
     */
    controlButtons?:
        | {
              size: ButtonProps['size'];
              position?: React.CSSProperties;
              withInsertButton: boolean;
              withOptionsButton: boolean;
              hitArea: {
                  className?: string;
                  style?: React.CSSProperties;
              };
          }
        | undefined;

    rightGutterComponent?:
        | {
              size: ButtonProps['size'];
              className?: string;
              style: React.CSSProperties;
              hitArea: {
                  className?: string;
                  style?: React.CSSProperties;
              };
          }
        | undefined;

    /**
     * Informs the block on the rendering mode it should use when rendering within the editor. This hook
     * is tasked with deciding the base render modes for the block's layout.
     */
    renderMode: LayoutRenderModes;

    /**
     * Whether the editor containing the block is read-only.
     */
    readOnly?: boolean;

    /**
     * Whether the block should be rendered full-width.
     */
    blockFullWidth?: boolean;
}

/**
 * Hook to compute the layout of a block wrapper.
 */
export function useBlockLayout(props: {
    ancestors: List<Block | Document | Fragment>;
    node: Block;
    isBlockSelection?: boolean;
    readOnly?: boolean;
    pageLayoutWidth?: PageLayoutOptionsWidth;
}): BlockLayout {
    const { ancestors, node, isBlockSelection = false, pageLayoutWidth, readOnly = false } = props;

    // Find the parent of the node, ignoring diff
    const parent = useParentSkippingDiff(ancestors);

    const isPageLayoutFullWidth =
        pageLayoutWidth === 'wide' && FULL_WIDTH_BLOCK_TYPES.includes(node.type);

    const hasFullWidthChildren = React.useMemo(() => {
        // Conditional blocks shouldn't prevent their children from being full-width.
        if (node.type === BLOCK_IF) {
            return node.blockNodes.some(
                (child) =>
                    FULL_WIDTH_BLOCK_TYPES.includes(child.type) &&
                    child.data.get('fullWidth') === true
            );
        }
        return false;
    }, [node]);

    const blockFullWidth =
        node.type === BLOCK_OPENAPI ||
        node.type === BLOCK_OPENAPI_OPERATION ||
        node.type === BLOCK_OPENAPI_SCHEMAS ||
        node.type === BLOCK_OPENAPI_WEBHOOK ||
        isPageLayoutFullWidth ||
        node.data.get('fullWidth') === true ||
        hasFullWidthChildren;

    const isAtRoot = parent.object === 'document';
    const isContentWidth = node.type === BLOCK_REUSABLE_CONTENT;

    // Blocks nested inside conditional blocks should be treated
    // as if they were at root level for width purposes.
    const isInConditionalBlock = parent && parent.type === BLOCK_IF;
    const shouldTreatAsRoot = isAtRoot || isInConditionalBlock;

    const renderMode = isContentWidth
        ? 'content-width'
        : shouldTreatAsRoot
          ? blockFullWidth
              ? 'full-width'
              : 'centered'
          : 'unstyled';

    const isTopLevelBlock =
        ancestors.size === 1 ||
        (ancestors.size === 2 && Block.isBlock(parent) && list.isList(parent));

    const isConflict = isAtRoot && conflicts.isConflictKey(node.key);

    const blockSpacing = React.useMemo(() => {
        const blockSpacing = getBlockSpacing(node, parent);
        if (isConflict) {
            blockSpacing.paddingTop += 16;
        }
        return blockSpacing;
    }, [parent, node, isConflict]);

    const blockLineHeight = React.useMemo(() => getBlockLineHeight(node), [node]);
    const rightGutterComponent = React.useMemo(() => {
        const rightGutterButtonSize = !isConflict
            ? getRightGutterButtonSize({ node, ancestors, parent })
            : null;
        const rightGutterGapSize = rightGutterButtonSize
            ? getRightGutterGapSize({ readOnly, nodeType: node.type, ancestors })
            : 0;
        const rightGutterOffset = getRightGutterOffset({ nodeType: node.type, readOnly });
        return rightGutterButtonSize
            ? {
                  size: rightGutterButtonSize.buttonSize,
                  className: 'relative',
                  style: {
                      top: (blockLineHeight - rightGutterButtonSize.height) / 2,
                      marginLeft: rightGutterGapSize,
                  },
                  hitArea: {
                      className: 'absolute h-full bg-transparent',
                      style: {
                          right: rightGutterOffset,
                          width: rightGutterGapSize,
                          marginRight: -1 * rightGutterGapSize,
                          top: 0,
                          paddingTop: blockSpacing.paddingTop,
                          paddingBottom: blockSpacing.paddingBottom,
                      },
                  },
              }
            : undefined;
    }, [
        blockLineHeight,
        blockSpacing.paddingTop,
        blockSpacing.paddingBottom,
        ancestors,
        node,
        parent,
        readOnly,
        isConflict,
    ]);

    const blockStyle = React.useMemo(
        () => getBlockStyleCSS(blockSpacing.paddingTop, blockSpacing.paddingBottom),
        [blockSpacing.paddingBottom, blockSpacing.paddingTop]
    );

    const controlButtons = React.useMemo(
        () =>
            getControlButtons({
                node,
                ancestors,
                lineHeight: blockLineHeight,
                blockSpacing,
                isTopLevelBlock,
                isBlockSelection,
            }),
        [ancestors, blockLineHeight, blockSpacing, isBlockSelection, isTopLevelBlock, node]
    );

    return React.useMemo(
        () => ({
            blockStyle,
            controlButtons,
            rightGutterComponent,
            renderMode,
            blockFullWidth,
        }),
        [blockStyle, controlButtons, renderMode, rightGutterComponent, blockFullWidth]
    );
}

/**
 * Slate plugin to ensure we always re-render the nodes when their spacing has changed.
 */
export const layoutPlugin: Plugin = {
    shouldNodeComponentUpdate(props, nextProps) {
        if (props.node.object === 'inline' || nextProps.node.object === 'inline') {
            return null;
        }

        const blockMargins = getBlockSpacing(
            props.node,
            props.parent as Block | Document | Fragment
        );
        const nextBlockMargins = getBlockSpacing(
            nextProps.node,
            nextProps.parent as Block | Document | Fragment
        );

        if (
            blockMargins.paddingTop !== nextBlockMargins.paddingTop ||
            blockMargins.paddingBottom !== nextBlockMargins.paddingBottom
        ) {
            return true;
        }

        return null;
    },
};

const BUTTON_SIZE = 24;
const CONTROLS_GAP = 4;
const CONTROLS_PADDING_X = 6;

export function useParentSkippingDiff(ancestors: List<AncestorNode>) {
    return React.useMemo(
        () =>
            ancestors
                .reverse()
                .skipUntil((ancestor) => ancestor.type !== BLOCK_DIFF)
                .first() as Block | Document | Fragment,
        [ancestors]
    );
}

function getControlButtons({
    node,
    ancestors,
    lineHeight,
    blockSpacing,
    isTopLevelBlock,
    isBlockSelection,
}: {
    node: Block;
    ancestors: List<Block | Document | Fragment>;
    lineHeight: number;
    blockSpacing: ReturnType<typeof getBlockSpacing>;
    isTopLevelBlock: boolean;
    isBlockSelection: boolean;
}) {
    const parent = ancestors.last();
    const withInsertButton = hasInsertButton({
        ancestors,
        node,
        isTopLevelBlock,
        isBlockSelection,
    });
    const withOptionsButton = hasOptionsButton({ ancestors, node, parent, isBlockSelection });
    const nbButtons = [withInsertButton, withOptionsButton].filter(Boolean).length;
    const buttonsWidth = nbButtons * BUTTON_SIZE + (nbButtons - 1) * CONTROLS_GAP;
    const leftPosition = -buttonsWidth - CONTROLS_PADDING_X * 2;
    const verticallyCentered = lineHeight / 2 - BUTTON_SIZE / 2;
    return {
        size: 'small' as const,
        withInsertButton,
        withOptionsButton,
        hitArea: {
            style: {
                paddingTop: blockSpacing.paddingTop,
                paddingBottom: blockSpacing.paddingBottom,
                paddingLeft: CONTROLS_PADDING_X,
                paddingRight: CONTROLS_PADDING_X,
                left: leftPosition,
                top: verticallyCentered,
            },
        },
    };
}

function hasInsertButton({
    ancestors,
    node,
    isBlockSelection,
    isTopLevelBlock,
}: {
    ancestors: List<Block | Document | Fragment>;
    node: Block;
    isBlockSelection: boolean;
    isTopLevelBlock: boolean;
}) {
    if (isBlockSelection) {
        return false;
    }

    if (ancestors.find((ancestor) => ancestor.type === BLOCK_TABLE)) {
        return false;
    }

    // A tab item does not have their own block insertion button.
    if (BLOCK_TABS_ITEM === node.type) {
        return false;
    }

    // We don't show insert buttons for list items, it's unreachable and confusing.
    if (LIST_BLOCKS.includes(node.type)) {
        return false;
    }

    // We show the insert button for block inside a if block since it acts a blocks wrapper.
    const parent = ancestors.last();
    if (parent && parent.type === BLOCK_IF) {
        return true;
    }

    return isTopLevelBlock;
}

function hasOptionsButton({
    ancestors,
    node,
    parent,
    isBlockSelection,
}: {
    ancestors: List<Block | Document | Fragment>;
    node: Block;
    parent: Block | Document | Fragment | null | undefined;
    isBlockSelection: boolean;
}) {
    // Node types that do not have their own block control button.
    if ([BLOCK_TABS_ITEM, BLOCK_COLUMN].includes(node.type)) {
        return false;
    }

    if (ancestors.find((ancestor) => ancestor.type === BLOCK_TABLE)) {
        return false;
    }

    // Hide options button for the first stepper step.
    if (node.type === BLOCK_STEPPER_STEP) {
        assert(parent, 'Stepper step block must have a parent');
        const firstNode = parent.nodes.first();
        assert(parent, 'Parent have at least this node');
        if (firstNode === node) {
            return false;
        }
    }

    // Individual list items have their own block control button, the parent
    // list does not need one.
    // However, for block selection, we do want to a control button for the list.
    if (!isBlockSelection && LIST_BLOCKS.includes(node.type)) {
        return false;
    }

    if (Block.isBlock(parent)) {
        if (
            parent?.isInFragment(node.key) &&
            parent.getFragmentParent(node.key)?.type === FRAGMENT_EXPANDABLE_TITLE
        ) {
            return false;
        }

        // Don't show for content of list items except for images.
        if (parent.type === BLOCK_LIST_ITEM && node.type !== BLOCK_IMAGES) {
            return false;
        }

        // Don't show for the first hint block.
        if (parent.type === BLOCK_HINT) {
            const firstNode = parent.nodes.first();
            assert(parent, 'Parent have at least this node');
            return firstNode !== node;
        }
    }

    return true;
}

const noRightGutterTypes = new Set([
    BLOCK_TABS_ITEM,
    LIST_BLOCKS,
    BLOCK_HINT,
    BLOCK_QUOTE,
    BLOCK_DIVIDER,
    BLOCK_COLUMN,
]);

/**
 * Get the size of the right gutter button.
 * Returning `null` means hiding the button.
 */
function getRightGutterButtonSize(props: {
    node: Block;
    ancestors: List<Block | Document | Fragment>;
    parent: Block | Document | Fragment | null | undefined;
}) {
    const { node, ancestors, parent } = props;
    // Some blocks are containers for other content and do not need a right-gutter button.
    if (noRightGutterTypes.has(node.type)) {
        return null;
    }

    if (ancestors.find((ancestor) => ancestor.type === BLOCK_COLUMN)) {
        return null;
    }

    // TODO this exception is because of space considerations in UI.
    // Could allow comments on expandable title but need to work out how to fit
    // the comments UI for both expandable block and the title in limited space.
    if (
        parent?.isInFragment(node.key) &&
        parent.getFragmentParent(node.key)?.type === FRAGMENT_EXPANDABLE_TITLE
    ) {
        return null;
    }

    return {
        buttonSize: 'small' as const,
        width: 24,
        height: 24,
    };
}

/**
 * Calculate how far there is between the end of the block and the right gutter.
 */
function getRightGutterGapSize(props: {
    readOnly: boolean;
    nodeType: string;
    ancestors: List<Block | Document | Fragment>;
}): number {
    const { ancestors, nodeType, readOnly } = props;

    let size = nodeType === BLOCK_TABLE && !readOnly ? 0 : 16;

    ancestors.forEach((ancestor) => {
        switch (ancestor.type) {
            case BLOCK_TABS_ITEM:
            case BLOCK_EXPANDABLE:
                size += 18;
                break;
            case BLOCK_HINT:
            case BLOCK_QUOTE:
                size += 16;
                break;
        }
    });

    return size;
}

/**
 * Get the offset of the right gutter. Usually this is 0, but some blocks may want to offset for e.g. UI around the block as with the Table's add-column button.
 */
function getRightGutterOffset({
    nodeType,
    readOnly,
}: {
    nodeType: Block['type'];
    readOnly: boolean;
}): number {
    if (nodeType === BLOCK_TABLE && !readOnly) {
        return -16;
    }
    return 0;
}

const PADDING_TOP_CLASS = {
    0: 'pt-0',
    4: 'pt-1',
    8: 'pt-2',
    16: 'pt-4',
    24: 'pt-6',
    40: 'pt-10',
    80: 'pt-20',
};

const PADDING_BOTTOM_CLASS = {
    0: 'pb-0',
    4: 'pb-1',
    8: 'pb-2',
    16: 'pb-4',
    24: 'pb-6',
    40: 'pb-10',
    80: 'pb-20',
};

function getBlockStyleCSS(paddingTop, paddingBottom) {
    const classNames: string[] = [];
    const style: React.CSSProperties = {};

    const top = PADDING_TOP_CLASS[paddingTop];
    const bottom = PADDING_BOTTOM_CLASS[paddingBottom];

    if (top === undefined) {
        style.paddingTop = paddingTop;
    } else {
        classNames.push(top);
    }

    if (bottom === undefined) {
        style.paddingBottom = paddingBottom;
    } else {
        classNames.push(bottom);
    }

    return {
        style,
        className: clsx(classNames),
    };
}
